import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../../services/auth.service';
import { PropertyService } from '../../services/property.service';
import { LesseeService } from '../../services/lessee.service';

@Component({
  selector: 'app-invoice-view',
  templateUrl: './invoice-view.component.html',
  styleUrls: ['./invoice-view.component.scss']
})
export class InvoiceViewComponent implements OnInit {
  @Input() 
  property;

  @Input()
  invoice;

  @Output()
  deleted = new EventEmitter<void>();

  lease;

  constructor(private propertyService: PropertyService, private authService: AuthService, private lesseeService: LesseeService, private router: Router) { }

  ngOnInit() {
    if(this.authService.isAdmin()) {
      this.propertyService.getActiveLease(this.property).then((res) => {
        this.lease = res;
      });
    } else {
      this.lesseeService.getActiveLease().then((res) => {
        this.lease = res;
      })
    }
  }

  hasStatementId(item: any): boolean {
    if (!item) return false;
    // Check both camelCase and snake_case, and also check for truthy values (not null, undefined, or empty string)
    const statementId = item.statementId || item.statement_id;
    const hasId = !!(statementId && statementId !== null && statementId !== undefined && statementId !== '');
    
    // Also check if invoice has payments with statement_id (invoices created from statements often have statement-linked payments)
    if (!hasId && item.payments && Array.isArray(item.payments)) {
      const hasStatementPayment = item.payments.some((p: any) => {
        const paymentStatementId = p.statementId || p.statement_id;
        return !!(paymentStatementId && paymentStatementId !== null && paymentStatementId !== undefined && paymentStatementId !== '');
      });
      if (hasStatementPayment) {
        return true;
      }
    }
    
    return hasId;
  }

  deleteInvoice() {
    if (this.hasStatementId(this.invoice)) {
      alert('Cannot delete invoice created from a statement. Remove the statement instead.');
      return;
    }
    if (!confirm('Are you sure you want to delete this invoice?')) {
      return;
    }
    this.propertyService.deleteInvoice(this.property, this.invoice).then(() => {
      this.deleted.emit();
    }).catch((error) => {
      console.error('Error deleting invoice:', error);
      const errorMessage = (error && error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
      if (errorMessage.includes('statement')) {
        alert('Cannot delete invoice created from a statement. Remove the statement instead.');
      } else {
        alert('Failed to delete invoice. Please try again.');
      }
    });
  }

  deletePdf(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!this.authService.isAdmin()) {
      return; // Lessees cannot delete PDFs
    }
    if (!confirm('Are you sure you want to delete this PDF?')) {
      return;
    }
    this.propertyService.deleteInvoicePdf(this.property, this.invoice.id).then((res: any) => {
      if(res) {
        this.invoice = res;
      }
    }).catch((error) => {
      console.error('Error deleting PDF:', error);
    });
  }

  generatePdf() {
    if (!this.authService.isAdmin()) {
      return; // Lessees cannot generate PDFs
    }
    this.propertyService.generateInvoicePdf(this.property, this.invoice.id).then((res: any) => {
      if(res) {
        this.invoice = res;
      }
    });
  }

  downloadPdf() {
    let promise;
    if(this.authService.isAdmin()) {
      promise = this.propertyService.getInvoicePdf(this.property, this.invoice.id);
    } else {
      promise = this.lesseeService.getInvoicePdf(this.invoice.id);
    }
    promise.then((blob: Blob) => {
      const fileName = this.buildDownloadFileName();
      this.saveBlobReliably(blob, fileName);
    }).catch((err) => {
      console.error('Failed to download invoice PDF:', err);
      alert('Unable to download the invoice PDF. Please try again.');
    });
  }

  private buildDownloadFileName(): string {
    const id = (this.invoice && this.invoice.id) || 'invoice';
    const due = (this.invoice && this.invoice.dueDate) ? String(this.invoice.dueDate).replace(/[^\w.-]/g, '') : '';
    const base = due ? `invoice-${due}-${id}` : `invoice-${id}`;
    return base + '.pdf';
  }

  /** Use Web Share with a named File when supported (works reliably on iOS Safari for Save to Files).
   * Fall back to <a download> for desktop and older browsers. */
  private saveBlobReliably(blob: Blob, fileName: string) {
    const nav: any = (typeof navigator !== 'undefined') ? navigator : null;
    const tryWebShareFile = async (): Promise<boolean> => {
      if (!nav || typeof nav.share !== 'function') {
        return false;
      }
      try {
        const file = new File([blob], fileName, { type: 'application/pdf', lastModified: Date.now() });
        const canShareFiles = !nav.canShare || nav.canShare({ files: [file] });
        if (canShareFiles) {
          await nav.share({ files: [file] });
          return true;
        }
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return true; // user dismissed, treat as handled
        }
      }
      return false;
    };

    tryWebShareFile().then((shared) => {
      if (shared) {
        return;
      }
      // Fallback: object URL + download attr (desktop works well)
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    });
  }

  viewPdf() {
    if (this.authService.isAdmin()) {
      this.router.navigate(['/invoice', this.invoice.id, 'pdf'], { queryParams: { propertyId: this.property.id } });
    } else {
      this.router.navigate(['/invoice', this.invoice.id, 'pdf']);
    }
  }
}
